import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  makeStyles,
  Tab,
  Tabs,
  Paper
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Build as BuildIcon,
  Event as EventIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Notes as NotesIcon,
  Business as BusinessIcon
} from '@material-ui/icons';
import moment from 'moment';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  closeButton: {
    color: theme.palette.primary.contrastText
  },
  detailSection: {
    marginBottom: theme.spacing(2)
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  detailIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  priorityChip: {
    fontWeight: 'bold'
  },
  statusChip: {
    marginLeft: theme.spacing(1)
  },
  tabContent: {
    paddingTop: theme.spacing(2),
    minHeight: 300
  },
  infoBox: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main
  },
  notesSection: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2)
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200
  }
}));

const WorkorderDetailModal = ({ open, workorder, onClose, url }) => {
  const classes = useStyles();
  const [fullWorkorder, setFullWorkorder] = useState(workorder);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open && workorder?._id) {
      fetchFullWorkorder();
    }
  }, [open, workorder]);

  const fetchFullWorkorder = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${url}/api/hmi/genericGetRequest?type=WORKORDER_DETAIL&workorderId=${workorder._id}`);
      if (response?.data?.success) {
        setFullWorkorder(response.data.data || workorder);
      }
    } catch (error) {
      console.error('Error fetching workorder details:', error);
      setFullWorkorder(workorder);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in progress':
        return 'primary';
      case 'scheduled':
        return 'info';
      case 'open':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getCrewDisplay = () => {
    if (!fullWorkorder?.crew) return 'Unassigned';
    const { name, foremanName } = fullWorkorder.crew;
    return `${name || ''}${foremanName ? ` (${foremanName})` : ''}`.trim() || 'Unassigned';
  };

  const renderDetails = () => (
    <Box>
      <Box className={classes.infoBox}>
        <Typography className={classes.sectionTitle}>Property Information</Typography>
        <Box className={classes.detailRow}>
          <BusinessIcon className={classes.detailIcon} />
          <Typography variant="body1">
            {fullWorkorder?.jobSite?.name || 'Unknown Property'}
          </Typography>
        </Box>
        {fullWorkorder?.jobSite?.address && (
          <Box className={classes.detailRow}>
            <LocationIcon className={classes.detailIcon} />
            <Typography variant="body2" color="textSecondary">
              {fullWorkorder.jobSite.address}
            </Typography>
          </Box>
        )}
      </Box>

      <Box className={classes.infoBox}>
        <Typography className={classes.sectionTitle}>Workorder Details</Typography>
        
        <Box className={classes.detailRow}>
          <AssignmentIcon className={classes.detailIcon} />
          <Typography variant="body2">
            <strong>Type:</strong> {fullWorkorder?.type || 'N/A'}
          </Typography>
        </Box>

        <Box className={classes.detailRow}>
          <WarningIcon className={classes.detailIcon} />
          <Typography variant="body2">
            <strong>Priority:</strong>
          </Typography>
          <Chip
            label={fullWorkorder?.priority || 'N/A'}
            size="small"
            color={getPriorityColor(fullWorkorder?.priority)}
            className={classes.priorityChip}
          />
        </Box>

        <Box className={classes.detailRow}>
          <CheckCircleIcon className={classes.detailIcon} />
          <Typography variant="body2">
            <strong>Status:</strong>
          </Typography>
          <Chip
            label={fullWorkorder?.status || 'Open'}
            size="small"
            color={getStatusColor(fullWorkorder?.status)}
            className={classes.statusChip}
          />
        </Box>
      </Box>

      <Box className={classes.infoBox}>
        <Typography className={classes.sectionTitle}>Assignment & Schedule</Typography>
        
        <Box className={classes.detailRow}>
          <PersonIcon className={classes.detailIcon} />
          <Typography variant="body2">
            <strong>Assigned To:</strong> {getCrewDisplay()}
          </Typography>
        </Box>

        <Box className={classes.detailRow}>
          <EventIcon className={classes.detailIcon} />
          <Typography variant="body2">
            <strong>Created:</strong> {moment(fullWorkorder?.createdDate).format('MMMM D, YYYY')}
          </Typography>
        </Box>

        <Box className={classes.detailRow}>
          <ScheduleIcon className={classes.detailIcon} />
          <Typography variant="body2">
            <strong>Scheduled:</strong> {moment(fullWorkorder?.scheduledDate).format('MMMM D, YYYY')}
          </Typography>
        </Box>

        {fullWorkorder?.completedDate && (
          <Box className={classes.detailRow}>
            <CheckCircleIcon className={classes.detailIcon} />
            <Typography variant="body2">
              <strong>Completed:</strong> {moment(fullWorkorder.completedDate).format('MMMM D, YYYY')}
            </Typography>
          </Box>
        )}
      </Box>

      {fullWorkorder?.description && (
        <Box className={classes.notesSection}>
          <Typography className={classes.sectionTitle}>
            <NotesIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Description
          </Typography>
          <Typography variant="body2">
            {fullWorkorder.description}
          </Typography>
        </Box>
      )}

      {fullWorkorder?.notes && (
        <Box className={classes.notesSection}>
          <Typography className={classes.sectionTitle}>
            <NotesIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Notes
          </Typography>
          <Typography variant="body2">
            {fullWorkorder.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderHistory = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" align="center">
        History information will be available here
      </Typography>
    </Box>
  );

  const renderRelatedInfo = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" align="center">
        Related workorders and issues will be displayed here
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center">
          <BuildIcon style={{ marginRight: 8 }} />
          <Typography variant="h6">
            Workorder Details
          </Typography>
        </Box>
        <IconButton
          edge="end"
          className={classes.closeButton}
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box className={classes.loadingWrapper}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Details" />
              <Tab label="History" />
              <Tab label="Related" />
            </Tabs>

            <Box className={classes.tabContent}>
              {activeTab === 0 && renderDetails()}
              {activeTab === 1 && renderHistory()}
              {activeTab === 2 && renderRelatedInfo()}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
        {fullWorkorder?.job && fullWorkorder?.client && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              window.open(`/clients/${fullWorkorder.client}/Job/${fullWorkorder.job}`, '_blank');
            }}
          >
            View Full Job
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WorkorderDetailModal;